// @ts-check
import { getAuthToken } from './useAuth.js';

/**
 * Centralized API fetch utility.
 * Handles auth token injection, JSON parsing, and error shaping.
 *
 * Usage in components:
 *   const { get, post, put, del } = useApi();
 *   const data = await get('/api/plants/get-all');
 *
 * Usage in stores (non-composable context):
 *   import { apiRequest, apiGet, apiPost } from '@/composables/useApi.js';
 *   const data = await apiGet('/api/projects');
 */

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * @param {string} url
 * @param {RequestInit & { headers?: Record<string, string> }} [options]
 * @returns {Promise<any>}
 */
async function apiRequest(url, options = {}) {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('NOT_AUTHENTICATED', 401, null);
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    ...options.headers,
  };

  // Only set Content-Type for requests with body
  if (options.body) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Send HttpOnly cookie for auth
  });

  // Handle non-JSON responses (e.g. 204 No Content)
  const contentType = response.headers.get('content-type');
  if (response.status === 204 || !contentType?.includes('application/json')) {
    if (!response.ok) {
      throw new ApiError(response.statusText || 'Request failed', response.status, null);
    }
    return null;
  }

  const data = await response.json();

  if (!response.ok) {
    throw new ApiError(
      data.error || data.message || 'Request failed',
      response.status,
      data
    );
  }

  return data;
}

function apiGet(url, params) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return apiRequest(url + qs);
}

function apiPost(url, body) {
  return apiRequest(url, { method: 'POST', body: JSON.stringify(body) });
}

function apiPut(url, body) {
  return apiRequest(url, { method: 'PUT', body: JSON.stringify(body) });
}

function apiDel(url) {
  return apiRequest(url, { method: 'DELETE' });
}

/**
 * Composable wrapper for use in Vue components.
 */
export function useApi() {
  return { request: apiRequest, get: apiGet, post: apiPost, put: apiPut, del: apiDel };
}

// Named exports for use in stores and non-composable contexts
export { apiRequest, apiGet, apiPost, apiPut, apiDel, ApiError };
